import {Platform, BackHandler} from 'react-native';
import Observables from '@happy-gastro/global-observables';
import AppKiller from '@happy-gastro/app-killer';

/**
 * Gracefully "close" / leave the app in a platform-safe way.
 *
 * This is the SAFE replacement for {@link killAppSafe}. The native
 * `@happy-gastro/app-killer` kills the process with
 * `Process.killProcess(Process.myPid())`, which the OS (and Sentry) reports as
 * an **abnormal termination / crash**, and which does not even exist on
 * iOS/web (the module resolves to `null`). Use this helper for every
 * user-facing "close the app" action (day open/close, logout) so the app
 * leaves cleanly instead of crashing.
 *
 * Per platform:
 *   - **Android** — `BackHandler.exitApp()`: the OS-sanctioned graceful exit
 *     (finishes the activity / moves to Home). NOT a process kill.
 *   - **iOS** — apps cannot exit programmatically (App Store guideline 2.4.4),
 *     so fire the caller-supplied soft-reload observable (e.g. `reloadAppMode`
 *     for logout → Login, `reloadDayOpeningClosing` after day close) to reach
 *     the intended end screen, matching Android's post-exit destination.
 *   - **Web / other** — hard `window.location.reload(true)`.
 *
 * @param {object} [options] Optional behaviour overrides.
 * @param {string} [options.reloadObservable] Name of the global observable to
 *   fire on iOS to reach the desired post-close screen without an OS exit.
 * @returns {boolean} True if a native exit was invoked (Android), false when
 *   the platform can only soft-reload (iOS/web).
 */
export function closeAppSafe({ reloadObservable } = {}) {
    if (Platform.OS === 'android') {
        // Graceful, OS-sanctioned exit — replaces the crash-like process kill.
        BackHandler.exitApp();

        return true;
    }
    if (Platform.OS === 'ios') {
        // iOS forbids programmatic exit; soft-reload to the intended screen.
        if (reloadObservable) {
            Observables.call(reloadObservable);
        }

        return false;
    }
    // Web / anything else: a full reload re-reads the now-updated runtime state.
    if (typeof window !== 'undefined' && window.location) {
        window.location.reload(true);
    }

    return false;
}

/**
 * Hard-terminate the Android app process via the native `AppKiller` module.
 *
 * @deprecated Prefer {@link closeAppSafe}. `AppKiller.killApp()` runs
 * `Process.killProcess(Process.myPid())`, an abrupt process death the OS/Sentry
 * treat as a crash (Sentry HAPPY-APP-D) — it is NOT a graceful close and does
 * nothing on iOS/web (the native module is `null` there). Retained only for the
 * rare case a genuine hard kill is required.
 *
 * The module resolves to `NativeModules.AppKiller`, which is `null` whenever the
 * native side is not linked (iOS, web, Expo Go, or an Android binary shipped
 * without it). This helper null-checks before invoking so it never throws.
 *
 * @returns {boolean} True if the native kill was invoked, false if unavailable.
 */
export function killAppSafe() {
    if (Platform.OS !== 'android') {
        // The native module only exists on Android; nothing to do elsewhere.
        return false;
    }
    if (AppKiller && typeof AppKiller.killApp === 'function') {
        AppKiller.killApp();

        return true;
    }
    console.warn('[appKiller] killApp unavailable on this platform/build — native AppKiller module is not linked.');

    return false;
}

export default closeAppSafe;
